import React from "react";
import "./ControlPanel.css";

interface System {
  systemId: string;
  systemName: string;
  totalUnlocks: number;
}

interface ControlPanelProps {
  graphType: "systems" | "locks";
  selectedSystemId: string;
  timeLimit: number;
  consecutiveLimit: number;
  systems: System[];
  isLoading: boolean;
  onGraphTypeChange: (type: "systems" | "locks") => void;
  onSystemSelect: (systemId: string) => void;
  onTimeLimitChange: (timeLimit: number) => void;
  onConsecutiveLimitChange: (consecutiveLimit: number) => void;
  onApplyFilters: () => void;
  onReset: () => void;
}

const timeOptions = [
  { value: 5000, label: "5 seconds" },
  { value: 10000, label: "10 seconds" },
  { value: 30000, label: "30 seconds" },
  { value: 60000, label: "1 minute" },
  { value: 180000, label: "3 minutes" },
  { value: 600000, label: "10 minutes" },
];

const consecutiveOptions = Array.from({ length: 9 }, (_, i) => ({
  value: i + 1,
  label: `${i + 2} events`, // Changed from "1 successive" to "2 events", etc.
}));

const ControlPanel: React.FC<ControlPanelProps> = ({
  graphType,
  selectedSystemId,
  timeLimit,
  consecutiveLimit,
  systems,
  isLoading,
  onGraphTypeChange,
  onSystemSelect,
  onTimeLimitChange,
  onConsecutiveLimitChange,
  onApplyFilters,
  onReset,
}) => {
  return (
    <div className="control-panel">
      <h3>Graph Controls</h3>

      {/* Graph Type Selection */}
      <div className="control-group">
        <label>View</label>
        <div className="button-group">
          <button
            className={graphType === "systems" ? "active" : ""}
            onClick={() => onGraphTypeChange("systems")}
            disabled={isLoading}
          >
            Systems
          </button>
          <button
            className={graphType === "locks" ? "active" : ""}
            onClick={() => onGraphTypeChange("locks")}
            disabled={isLoading}
          >
            Access Points
          </button>
        </div>
      </div>

      {/* System Selection (only for locks graph) */}
      {graphType === "locks" && (
        <div className="control-group">
          <label htmlFor="system-select">Select System</label>
          <select
            id="system-select"
            value={selectedSystemId}
            onChange={(e) => onSystemSelect(e.target.value)}
            disabled={isLoading}
          >
            <option value="">Choose a system...</option>
            {systems.map((system) => (
              <option key={system.systemId} value={system.systemId}>
                {system.systemId} - {system.systemName} (
                {system.totalUnlocks.toLocaleString()} events)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Access Dimension Selection (moved before Access Timeframe) */}
      <div className="control-group">
        <label htmlFor="consecutive-limit">Access Dimension</label>
        <select
          id="consecutive-limit"
          value={consecutiveLimit}
          onChange={(e) => onConsecutiveLimitChange(Number(e.target.value))}
          disabled={isLoading}
        >
          {consecutiveOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Access Timeframe Selection (moved after Access Dimension) */}
      <div className="control-group">
        <label htmlFor="time-limit">Timeframe</label>
        <select
          id="time-limit"
          value={timeLimit}
          onChange={(e) => onTimeLimitChange(Number(e.target.value))}
          disabled={isLoading}
        >
          {timeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Info Panel */}
      <div className="info-panel">
        <h4>Current Settings</h4>
        <ul>
          <li>
            <strong>Graph:</strong>{" "}
            {graphType === "systems"
              ? "System-level"
              : `Locks in ${selectedSystemId || "No system selected"}`}
          </li>
          <li>
            <strong>Time Window:</strong>{" "}
            {timeOptions.find((opt) => opt.value === timeLimit)?.label}
          </li>
          <li>
            <strong>Min Frequency:</strong> {consecutiveLimit}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ControlPanel;
